/**
 * Å invitere et menneske inn i Tvitvi — hele veien fra adressen til medlemskapet.
 *
 * ---------------------------------------------------------------------------------------------
 * HVORFOR DENNE FILA FINNES VED SIDEN AV `contacts.ts`.
 *
 * `invitationDtoSchema` bor i `contacts.ts`, fordi invitasjonen ble skrevet som en handling PÅ en
 * kontakt: `POST /v1/contacts/:id/invite`. Det er fortsatt riktig — en invitasjon peker alltid på
 * et menneske som allerede finnes i registeret, og det er nettopp det som gjør at den som takker
 * ja aldri blir en dublett.
 *
 * Det som IKKE er en kontakthandling er resten av kjeden: lista over de som ikke har svart, å
 * sende på nytt, å trekke tilbake, og innløsingen — som per definisjon skjer UTENFOR
 * organisasjonen, hos noen som kanskje ikke har en Tvitvi-konto i det hele tatt. De fire hører
 * sammen og hører ikke hjemme i kontaktregisteret.
 *
 * ---------------------------------------------------------------------------------------------
 * TOKENET VISES ÉN GANG, SOM BYRÅKOBLINGEN GJØR DET (vedtak 93/99, `organization-links.ts`).
 *
 * Basen holder bare SHA-256 av tokenet, så svaret på en invitasjon er det eneste øyeblikket
 * strengen finnes utenfor mottakerens innboks. Det er derfor `InvitationCreatedResponse` bærer
 * den — og derfor skjermen som kaller endepunktet MÅ si at lenken ikke kommer igjen.
 *
 * Huset hadde alt dette mønsteret for lenken mellom to hus. To mønstre for det samme er ett for
 * mange, så dette er det samme: rått token i svaret, hash i basen, ingen vei tilbake.
 */
import { z } from 'zod';
import { pageCursorFields } from '../http/pagination.js';

import { localeSchema } from '../locale.js';
import { orgRoleSchema } from '../enums.js';
import { acceptedDocumentSchema } from './auth.js';

import { invitationDtoSchema } from './contacts.js';
import { emailDeliveryOutcomeSchema, messageDeliverySchema } from './messaging.js';

/**
 * `member` gir et medlemskap med en rolle. `artist` og `external` gir ingen rad i organisasjonen:
 * personen godtar en kobling til kontakten der og lander i sin personlige konto (0377, sak 216;
 * 0460, sak 232). Bare `member` har en rolle — `role` er null for de to andre.
 */
export const INVITATION_KINDS = ['member', 'artist', 'external'] as const;
export type InvitationKind = (typeof INVITATION_KINDS)[number];
export const invitationKindSchema = z.enum(INVITATION_KINDS);

/**
 * ROLLENE MAN KAN INVITERE TIL (sak 232). `external` er ikke en av dem lenger: en ekstern får en
 * personlig konto (`POST /v1/invitations/external`), ikke et medlemskap. Siden 0465 er lista den
 * samme som `ORG_ROLES`; den står for seg fordi det er et eget spørsmål (hva man kan invitere til).
 */
export const INVITABLE_ROLES = ['superadmin', 'admin', 'member', 'limited'] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];
export const invitableRoleSchema = z.enum(INVITABLE_ROLES).meta({ id: 'InvitableRole' });

const shortTextSchema = z.string().trim().max(200);

/**
 * Invitasjonen sendt fra «Inviter til Tvitvi»-knappen: en adresse og en rolle.
 *
 * `email` OG IKKE `contactId`, og det er hele poenget med denne døra. Den første en ny kunde gjør
 * er å hente inn kollegaene sine, og ingen av dem står i registeret ennå. API-et slår opp
 * adressen, gjenbruker mennesket hvis det finnes (`app.resolve_person`), og oppretter kontakten
 * hvis det ikke gjør det — så registeret er sant etterpå uansett hvilken vei man kom inn.
 *
 * `role` er underlagt vedtak 0007 i `can()`: ingen inviterer på eller over sitt eget nivå.
 */
export const createInvitationRequestSchema = z
  .object({
    email: z.email().max(320),
    /** Navnet kontakten får hvis den må opprettes. Uten det brukes adressen. */
    /* QA15 2b: én navnemodell i hele appen — fornavn og etternavn, aldri ett felt. */
    givenName: shortTextSchema.nullish(),
    familyName: shortTextSchema.nullish(),
    role: invitableRoleSchema,
    jobTitle: shortTextSchema.nullish(),
    responsibility: shortTextSchema.nullish(),
    /**
     * HVEM PERSONEN RAPPORTERER TIL (QA12 #7).
     *
     * Et MEDLEMSKAP og ikke et menneske: den man rapporterer til, gjør det i dette huset. Den
     * samme personen kan være sjefen din her og kollegaen din i et annet, og en peker på personen
     * ville sagt det første i begge.
     */
    reportsToMembershipId: z.uuid().nullish(),
  })
  .meta({ id: 'CreateInvitationRequest' });
export type CreateInvitationRequest = z.infer<typeof createInvitationRequestSchema>;

/**
 * Svaret på en ny invitasjon — og det bærer det rå tokenet, ÉN gang.
 *
 * En egen kontrakt framfor et anonymt objekt fordi det er et svar en klient parser (prinsipp 3):
 * et endepunkt som returnerer en form ingen har skrevet ned, kan endre form uten at noe sier fra.
 */
export const invitationCreatedResponseSchema = z
  .object({
    invitation: invitationDtoSchema,
    /** Rått, ikke hashet. Vises én gang og lagres aldri av klienten. */
    token: z.string(),
    /** E-posten med lenken: lagt i køen, eller stoppet av mottakerlista (sak 7). */
    emailDelivery: emailDeliveryOutcomeSchema,
  })
  .meta({ id: 'InvitationCreatedResponse' });
export type InvitationCreatedResponse = z.infer<typeof invitationCreatedResponseSchema>;

/**
 * En rad i «venter på svar»-lista.
 *
 * Den bærer NAVNET og ikke bare adressen, fordi det er navnet den som inviterte husker. `email`
 * er et øyeblikksbilde tatt da invitasjonen ble sendt — personens adresser kan endres etterpå, og
 * lenken gjelder fortsatt den som ble invitert.
 */
export const pendingInvitationDtoSchema = z
  .object({
    id: z.uuid(),
    contactId: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    email: z.email(),
    /** Null for en artist- eller ekstern invitasjon: de gir ikke et medlemskap. */
    role: orgRoleSchema.nullable(),
    kind: invitationKindSchema,
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    invitedByPersonId: z.uuid().nullable(),
    invitedByName: z.string().nullable(),
    /** Sann når utløpstiden har passert. Raden blir stående til den trekkes eller sendes på nytt. */
    expired: z.boolean(),
    /** Jobb 2 (0491): e-posten med lenken — siste som ikke er trukket. Null før noe er lagt i køen. */
    delivery: messageDeliverySchema.nullable(),
  })
  .meta({ id: 'PendingInvitation' });
export type PendingInvitationDto = z.infer<typeof pendingInvitationDtoSchema>;

/** Ikke paget: et hus har titalls ubesvarte invitasjoner, ikke tusener, og lista leses hel. */
export const pendingInvitationListResponseSchema = z
  .object({ items: z.array(pendingInvitationDtoSchema), ...pageCursorFields })
  .meta({ id: 'PendingInvitationListResponse' });
export type PendingInvitationListResponse = z.infer<typeof pendingInvitationListResponseSchema>;

/**
 * Det den som klikker lenken får se FØR innlogging.
 *
 * Med vilje magert, av samme grunn som byråkoblingens forhåndsvisning: en invitasjonsadresse er
 * noe noen kan gjette på, og alt som står her lekker til den som gjetter riktig. Hvem som
 * inviterer, til hvilken rolle, og hvilken adresse man må logge inn med — det er det man trenger
 * for å bestemme seg, og adressen er ikke en hemmelighet for den som alt har lenken.
 */
export const invitationPreviewSchema = z
  .object({
    organizationName: z.string(),
    email: z.email(),
    /** Null for `artist` og `external`: siden viser ingen rolle for dem. */
    role: orgRoleSchema.nullable(),
    expiresAt: z.iso.datetime(),
    /** Sann bare når invitasjonen fortsatt kan løses inn. */
    open: z.boolean(),
    /*
     * DET AVSENDEREN ALLEREDE SKREV INN (QA14 #1).
     *
     * Atle: «Den som inviterer må skrive inn navnet, men mottakeren må skrive det på nytt.»
     * Navnet sto i basen hele tiden — på kontaktraden invitasjonen peker på — men siden kunne
     * ikke se det. Alle fire er redigerbare av mottakeren; de er et utgangspunkt, ikke en lås.
     */
    displayName: z.string().nullable(),
    /* QA15 2b: de to delene, saa sida slipper aa dele en streng for aa fylle ut to felt. */
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    phone: z.string().nullable(),
    jobTitle: z.string().nullable(),
    responsibility: z.string().nullable(),
    /* QA15 2a: boksen skal si «Rapporterer til: Atle», ikke bare vise to nakne linjer. */
    reportsToName: z.string().nullable(),
    /*
     * K-19: HAR MOTTAKEREN KONTO FRA FØR?
     *
     * Atle: «Systemet vet e-posten fra invitasjonen og sjekker selv om kontoen finnes …
     * Brukeren skal ikke måtte velge.» Knappen «Jeg har konto fra før» ba om noe systemet visste.
     *
     * Lekker ingenting: adressen står i invitasjonen, og den som holder tokenet vet den fra før.
     * Svaret sier altså ikke noe til noen som ikke visste det.
     */
    hasAccount: z.boolean(),
    /*
     * KONTOEN FINNES, MEN UTEN NAVN (1327). En registrering som stoppet etter at kontoen var laget,
     * etterlot e-posten som navn. Da spør siden om navn og telefon, selv om kontoen finnes.
     */
    needsProfile: z.boolean(),
    /** «som artist» (sak 216) eller «personlig konto» (sak 232) i stedet for en rolle. */
    kind: invitationKindSchema,
    /** Hvem som sendte invitasjonen — den eksterne varianten av siden sier det (K-11/K-19). */
    invitedByName: z.string().nullable(),
  })
  .meta({ id: 'InvitationPreview' });
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

/**
 * «HAR DENNE ADRESSEN ALLEREDE EN KONTO?» — sett fra den som inviterer (sak 194).
 *
 * Ett boolsk svar, og med vilje ikke mer. Navnet står ikke her: en dialog som ga et navn på en
 * vilkårlig adresse ville vært et oppslagsverk over alle mennesker i Tvitvi, og det er en lekkasje
 * på tvers av hus uansett hvor pent den pakkes inn. Se `app.email_has_account` (0353).
 */
export const invitationAccountCheckSchema = z
  .object({ hasAccount: z.boolean() })
  .meta({ id: 'InvitationAccountCheck' });
export type InvitationAccountCheck = z.infer<typeof invitationAccountCheckSchema>;

export const invitationAccountCheckQuerySchema = z
  .object({ email: z.string().trim().min(3).max(320) })
  .meta({ id: 'InvitationAccountCheckQuery' });
export type InvitationAccountCheckQuery = z.infer<typeof invitationAccountCheckQuerySchema>;

/**
 * Innløsingen. Tokenet er det eneste som identifiserer invitasjonen: den som løser inn står per
 * definisjon utenfor organisasjonen, så ingen id derfra ville vært lesbar.
 */
/**
 * ALT INNLOESNINGEN TRENGER, I ETT KALL (QA15).
 *
 * Kroppen bar bare tokenet, og klienten gjorde tre kall til etterpaa: sesjon med samtykke,
 * profil med telefon og spraak, og bytte av hus. Fire kall der ett kunne feile og la resten staa
 * ugjort — som er noeyaktig det som skjedde, fire runder paa rad.
 *
 * Feltene er valgfrie fordi den ene veien inn ikke spoer om dem: har du konto fra foer (K-19),
 * tegner sida bare passordfeltet, og da skal navnet, telefonen og spraaket du ALLEREDE har staa
 * urort. Et felt som ikke ble vist, sendes ikke.
 */
export const redeemInvitationRequestSchema = z
  .object({
    token: z.string().min(20).max(200),
    givenName: shortTextSchema.nullish(),
    familyName: shortTextSchema.nullish(),
    phone: z.string().trim().max(50).nullish(),
    preferredLocale: localeSchema.nullish(),
    /**
     * TITTEL (B3, 253.26): den samme som «Opprett organisasjonen» spør om. Skrives på medlemskapet
     * bare når den som inviterte ikke satte en — da er det et svar, ikke en overstyring.
     */
    jobTitle: z.string().trim().min(1).max(200).nullish(),
    acceptedDocuments: z.array(acceptedDocumentSchema).max(4).optional(),
  })
  .meta({ id: 'RedeemInvitationRequest' });
export type RedeemInvitationRequest = z.infer<typeof redeemInvitationRequestSchema>;

/**
 * Hva som faktisk ble til: medlemskapet. Klienten trenger organisasjonen for å kunne be om den
 * på neste kall (`X-Tvitvi-Organization`), og navnet for å kunne si hvor man havnet.
 */
export const redeemInvitationResponseSchema = z
  .object({
    organizationId: z.uuid(),
    organizationName: z.string(),
    role: orgRoleSchema,
    membershipId: z.uuid(),
    /** For en artist og en ekstern er huset personens eget, ikke organisasjonens som inviterte. */
    kind: invitationKindSchema,
  })
  .meta({ id: 'RedeemInvitationResponse' });
export type RedeemInvitationResponse = z.infer<typeof redeemInvitationResponseSchema>;

/**
 * «INVITER SOM ARTIST» FRA BYRÅETS BANDMEDLEM (sak 216). Tre utfall, og skjermen sier hvilket:
 *   `invited`   — ingen konto på adressen: en invitasjon, lenken står i `token` ÉN gang.
 *   `requested` — adressen har en konto: en forespørsel personen godtar selv (sak 137).
 *   `member`    — personen er alt medlem av huset og ser konsertene sine uten noe mer.
 */
export const inviteArtistResponseSchema = z
  .object({
    outcome: z.enum(['invited', 'requested', 'member']),
    invitation: invitationDtoSchema.nullable(),
    token: z.string().nullable(),
    /**
     * Kommende konsertrader hos festivalene som pekte på en kopi av medlemmet og nå peker på den
     * riktige personen (sak 216.12, 0403). Tidligere konserter telles aldri: de røres ikke.
     */
    relinkedConcerts: z.number().int().nonnegative(),
    /** E-posten med lenken når `outcome` er `invited`, ellers null (sak 7). */
    emailDelivery: emailDeliveryOutcomeSchema.nullable(),
  })
  .meta({ id: 'InviteArtistResponse' });
export type InviteArtistResponse = z.infer<typeof inviteArtistResponseSchema>;

/**
 * «INVITER SOM EKSTERN» (sak 232, T2). En ekstern er ikke medlem av noe: invitasjonen gir en
 * personlig konto (216-modellen), og det organisasjonen deler med personen kommer som delinger (P3).
 *
 * To utfall — det fryste svaret fra `ExternalInvitationService.invite(contactId)` (PLAN 1.4):
 *   invited     — ingen konto på adressen: en invitasjon, lenken står i `token` ÉN gang. En åpen
 *                 ekstern invitasjon til den samme personen får et nytt token, som «Send på nytt».
 *   has_account — adressen har en innlogging: ingen invitasjon. Kontakten peker nå på personen med
 *                 kontoen, og en deling kommer rett i Innboks (232.6).
 * `personId` er et tillegg til den fryste formen: personen en deling skal gis til, i begge utfall.
 */
export const inviteExternalRequestSchema = z
  .object({ contactId: z.uuid() })
  .meta({ id: 'InviteExternalRequest' });
export type InviteExternalRequest = z.infer<typeof inviteExternalRequestSchema>;

export const externalInvitationResultSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('invited'),
    invitationId: z.uuid(),
    personId: z.uuid(),
    /** Rått, ikke hashet. Vises én gang og lagres aldri av klienten. */
    token: z.string(),
    expiresAt: z.iso.datetime(),
  }),
  z.object({ outcome: z.literal('has_account'), personId: z.uuid() }),
]);
export type ExternalInvitationResult = z.infer<typeof externalInvitationResultSchema>;

/**
 * Det samme svaret over HTTP, flatt: en DTO-klasse kan ikke bygges av en union, så feltene som
 * bare finnes i `invited` er null i `has_account` — samme form som `InviteArtistResponse`.
 */
export const externalInvitationResponseSchema = z
  .object({
    outcome: z.enum(['invited', 'has_account']),
    personId: z.uuid(),
    invitationId: z.uuid().nullable(),
    /** Rått, ikke hashet. Vises én gang og lagres aldri av klienten. */
    token: z.string().nullable(),
    expiresAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'ExternalInvitationResponse' });
export type ExternalInvitationResponse = z.infer<typeof externalInvitationResponseSchema>;

export function toExternalInvitationResponse(
  result: ExternalInvitationResult,
): ExternalInvitationResponse {
  return result.outcome === 'invited'
    ? {
        outcome: 'invited',
        personId: result.personId,
        invitationId: result.invitationId,
        token: result.token,
        expiresAt: result.expiresAt,
      }
    : {
        outcome: 'has_account',
        personId: result.personId,
        invitationId: null,
        token: null,
        expiresAt: null,
      };
}
